import { Box, Button, ButtonBase } from '@mui/material';
import { useTheme } from '@mui/material';
import LightModeIcon from '../../custom-icons/LightModeIcon';
import DarkModeIcon from '../../custom-icons/DarkModeIcon';

export type ThemeSelectorProps = {
  onThemeChange: (theme: string) => void;
  value?: string;
  size?: 'small' | 'large';
};

const ThemeSelector = ({ onThemeChange, value = 'dark', size = 'small' }: ThemeSelectorProps) => {
  const MuiTheme = useTheme();

  const isSmall = size === 'small';

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          position: 'absolute',
          clipPath: isSmall ? 'inset(0 0% 0 0)' : 'inset(0 100% 0 0)',
          transition: 'clip-path 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <ButtonBase
          sx={{
            display: 'flex',
            padding: '9px 16px',
            borderRadius: '2px',
            height: '56px',
            color: 'text.secondary',
            '&:hover': {
              background: `linear-gradient(0deg, ${
                MuiTheme.palette.opac1
              } 0%,  ${MuiTheme.palette.opac1} 100%),  ${
                value === 'dark' ? MuiTheme.palette.background.paperElevation0 : '#F0F6FF'
              }`,
              boxShadow: MuiTheme.palette.elevations.hoverButton,
            },
          }}
          onClick={() => onThemeChange(value === 'dark' ? 'light' : 'dark')}
        >
          {value === 'dark' ? <DarkModeIcon /> : <LightModeIcon />}
        </ButtonBase>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '4px',
          padding: '9PX 16px',
          position: 'absolute',
          width: '100%',
          clipPath: !isSmall ? 'inset(0 0% 0 0)' : 'inset(0 100% 0 0)',
          transition: 'clip-path 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {[
          { id: 'light', icon: LightModeIcon },
          { id: 'dark', icon: DarkModeIcon },
        ].map((item) => {
          return (
            <Button
              key={item.id}
              variant="contained"
              className={value === item.id && 'active'}
              onClick={() => onThemeChange(item.id)}
              fullWidth
              sx={{
                borderRadius: '2px',
                background: MuiTheme.palette.background.paperElevation0,
                color: 'text.secondary',
                boxShadow: MuiTheme.palette.elevations.button,
                '&.active': {
                  background: MuiTheme.palette.opacblack2,
                  boxShadow: MuiTheme.palette.elevations.secondaryButtonPressed2,
                  color: MuiTheme.palette.text.selectedbutton,
                  '&:hover': {
                    background: MuiTheme.palette.opacblack3,
                    boxShadow: MuiTheme.palette.elevations.hoverButtonPressed,
                  },
                },
                '&:hover': {
                  background: `linear-gradient(0deg, ${MuiTheme.palette.opac1} 0%, ${MuiTheme.palette.opac1} 100%), ${MuiTheme.palette.background.paperElevation0}`,
                  boxShadow: MuiTheme.palette.elevations.button,
                },
              }}
            >
              <item.icon />
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default ThemeSelector;
